import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Lock } from "lucide-react";

const LOGO = "/logo.png";

export default function Login() {
  const { t } = useLang();
  const { login, loading } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    const res = await login(username.trim(), password);
    if (res.ok) {
      toast.success("Welcome " + username);
      navigate("/portal");
    } else {
      toast.error(t.login.error);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute -top-40 -left-40 w-96 h-96 bg-primary/20 blur-[100px] rounded-full" />
      <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-primary/10 blur-[100px] rounded-full" />

      <div className="relative w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-8" data-testid="login-back-link">
          <ArrowLeft className="h-4 w-4" /> {t.login.back}
        </Link>

        <div className="rounded-2xl border border-border bg-card p-8 shadow-2xl">
          <div className="flex items-center justify-center mb-8">
            <img src={LOGO} alt="logo" className="h-16 w-auto" />
          </div>

          <div className="flex items-center gap-2 text-primary mb-2">
            <Lock className="h-4 w-4" />
            <div className="text-xs uppercase tracking-widest font-bold">Secure Access</div>
          </div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>{t.login.title}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t.login.subtitle}</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="username">{t.login.username}</Label>
              <Input
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                required
                className="bg-background border-border h-11"
                data-testid="login-username-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t.login.password}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                className="bg-background border-border h-11"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-11"
              data-testid="login-submit-btn"
            >
              {loading ? "..." : t.login.submit}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
